import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { number, instanceOf, func } from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import GenericEditWithCancelSave from '../../common/GenericEditWithCancelSave';
import RowWarningIcon from '../shared/icons/RowWarningIcon';
import { DATAHUB_SAMPLE_WARN_COUNT } from '../../../actions/urlTypes';
import {
  removeTargetRow,
  updateExpressionName,
  setIsEditTarget,
  setDupicateTargetNode
} from '../../../actions/deActions/deBuildActions';
import { getLabel } from '../../../util/autocodeUtils';
import { numberWithCommas } from '../../../util/CommonUtils';

import ContextMenu from './ContextMenu';

const PreviewBodyRow = ({ target, rowIndex, handleEditExpression }) => {
  const [inputText, setInputText] = useState({});
  const [editing, setEditing] = useState(false);
  const dispatch = useDispatch();
  const { partsToShow, deBuildTargets } = useSelector(state => ({
    partsToShow: state.deBuild.partsToShow,
    deBuildTargets: state.deBuild.deBuildTargets,
  }));

  useEffect(() => {
    if (editing) {
      inputText.focus();
      inputText.select();
    }
  }, [inputText]);

  const handleEditName = () => {
    setEditing(true);
  };

  useEffect(() => {
    if(target.isDuplicateTarget && rowIndex - 1 === 0){
      setEditing(true);
    }
  }, []);

  const handleDelete = () => {
    dispatch(removeTargetRow(rowIndex - 1));
  };

  const _handleAction = actionType => {
    if (actionType === 'EDIT_NAME') {
      handleEditName();
    } else if (actionType === 'DELETE') {
      handleDelete();
    } else if (actionType === 'DUPLICATE') {
      dispatch(setDupicateTargetNode(target));
    } else {
      handleEditExpression(target, rowIndex);
    }
  };

  const saveInput = async (textValue, index) => {
    const targetRow = cloneDeep(deBuildTargets[index]);
    const wnArr = deBuildTargets[index].ui.split('_');
    const uniqueId = `${deBuildTargets[index].pn}_${textValue}_${
      wnArr[wnArr.length - 1]
    }`;
    targetRow.tn = textValue;
    const targetArr = [];
    targetArr.push(targetRow);
    const duplicateData = deBuildTargets.filter(item => item.ui.toLowerCase() === uniqueId.toLowerCase());
    dispatch(setIsEditTarget(true))
    if (duplicateData.length > 0) {
      dispatch(updateExpressionName(textValue, index, targetArr, true, false));
    } else {
      const res = await dispatch(updateExpressionName(textValue, index, targetArr, false, false));
      if(res){
        setEditing(false)
      }
    }
  };
  const _setEditing = async (actionState = false) => {
    const targetRow = cloneDeep(deBuildTargets[rowIndex - 1]);
    const targetArr = [];
    targetArr.push(targetRow);
    if(actionState === "cancel"){
      dispatch(updateExpressionName(targetRow.actualLabel, rowIndex - 1, targetArr, false, true));
      setEditing(false)
    } else if( !targetRow.isFailed && !targetRow.isDuplicate){
      setEditing(false)
    }
  }

  const sampletextClass =
    target.sample < DATAHUB_SAMPLE_WARN_COUNT
      ? 'de--build--preview-samplewarning'
      : 'de--build--preview-sampletext';
  return (
    <div className="de--build--preview-rowcard" key={rowIndex}>
      {target && (
        <div>
          {editing ? (
            <div className="de--build--preview-rowcard-edit">
              <GenericEditWithCancelSave
                data-test="DeBuildRowEdit"
                setEditing={_setEditing}
                initValue={getLabel(target, partsToShow)}
                saveInput={textValue => saveInput(textValue, rowIndex - 1)}
                setInputText={setInputText}
                isBuildEdit
              />
            </div>
          ) : (
            <div className="de--build--preview-row">
              <span
                className="de--build--preview-targetname"
                title={getLabel(target, partsToShow)}
              >
                <span
                  data-test="SpanEditBtn"
                  onDoubleClick={() => _handleAction('EDIT_NAME')}
                >
                  {getLabel(target, partsToShow)}
                </span>
              </span>
              <span className={sampletextClass}>
                {numberWithCommas(target.sample).toLocaleString()}
              </span>
              <span className="de--build--preview-weightedtext">
                {numberWithCommas(target.weighted).toLocaleString()}
              </span>
              <span className="de--build--preview-contextmenu">
                <ContextMenu
                  data-test="labelcontextmenu"
                  onClick={_handleAction}
                />
              </span>
            </div>
          )}
          <div className="de--build--preview-subrow">
            <span className="de--build--preview-study" title={target.sn}>
              {target.sn}
            </span>
          </div>
          <div className="de--build--preview-subrow">
            <div className="de--build--preview-warningmsg">
              {target.sample < DATAHUB_SAMPLE_WARN_COUNT && (
                <span>
                  For best results, use sample sizes above{' '}
                  {DATAHUB_SAMPLE_WARN_COUNT.toLocaleString()}
                </span>
              )}
            </div>
            { ( target.isFailed || target.isDuplicate ) && (
              <span className="de--build--preview-warningicon">
                <RowWarningIcon />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

PreviewBodyRow.propTypes = {
  target: instanceOf(Object),
  rowIndex: number,
  handleEditExpression: func,
};

export default PreviewBodyRow;
